/**
 * _shared/agentHelpers.ts
 * ────────────────────────────────────────────────────────────────────────
 * Funciones puras y hooks que transforman el objeto `agent` crudo en
 * valores listos para renderizar.
 *
 * Filosofía:
 *  - Los templates NO leen `agent.show_*` ni arman nombres/URLs a mano:
 *    invocan un helper y reciben la forma ya resuelta.
 *  - Todos los helpers respetan los flags `show_*` como fuente de verdad.
 *  - Las dos marcas del agente (PERSONAL y CORPORATIVA) se resuelven
 *    por SEPARADO porque viven en zonas distintas de la landing y nunca
 *    compiten por el mismo espacio.
 *  - El sello NOM-247 se centraliza en el hook `useNomSeal` — ningún
 *    template replica fetch ni lógica de estado propia.
 *
 * Creado: 17 Abril 2026
 * ────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react'
import type { AgentFull, LegalDocType } from './types'
import {
  SUBDOMAIN_FALLBACK,
  DEFAULT_CHAT_DELAY,
  DEFAULT_SHOW_IA,
  API_ROUTES,
  NOM_CERT_VERIFY_URL,
  NOM_SEAL_ASSET_PATH,
  NOM_SEAL_COPY,
} from './constants'

// ══════════════════════════════════════════════════════════════════════════
// getSubdomain
// ══════════════════════════════════════════════════════════════════════════

/**
 * Retorna el subdominio del hostname actual. En entornos de desarrollo
 * local (localhost, 127.0.0.1, IPs) retorna `SUBDOMAIN_FALLBACK` para
 * que la landing no explote al probar en `http://localhost:3000`.
 *
 * @returns Subdominio del agente o fallback en local.
 *
 * @example
 *   const subdomain = getSubdomain()
 *   // En 'juan-perez.innovagent.tech' → 'juan-perez'
 *   // En 'localhost'                  → 'demo'
 */
export function getSubdomain(): string {
  if (typeof window === 'undefined') return SUBDOMAIN_FALLBACK

  const hostname = window.location.hostname

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return SUBDOMAIN_FALLBACK
  }

  // IPs literales: cuatro grupos de dígitos separados por puntos
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return SUBDOMAIN_FALLBACK
  }

  const parts = hostname.split('.')
  // Sin subdominio real (ej. dominio raíz 'innovagent.tech'): fallback
  if (parts.length < 3) return SUBDOMAIN_FALLBACK

  return parts[0]
}

// ══════════════════════════════════════════════════════════════════════════
// formatPrice
// ══════════════════════════════════════════════════════════════════════════

/**
 * Formatea un precio con separadores de miles en-US, sin decimales, y
 * con moneda SIEMPRE explícita al final.
 *
 * @param price - Número bruto
 * @param currency - 'MXN' o 'USD'
 * @returns String formateado "$1,500,000 MXN"
 *
 * @example
 *   formatPrice(1500000, 'MXN')  // "$1,500,000 MXN"
 *   formatPrice(250000, 'USD')   // "$250,000 USD"
 */
export function formatPrice(price: number, currency: 'MXN' | 'USD'): string {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)
  return `$${formatted} ${currency}`
}

// ══════════════════════════════════════════════════════════════════════════
// resolveContactChannels
// ══════════════════════════════════════════════════════════════════════════

export interface ResolvedContactChannels {
  /** Flags efectivos: el show_* del agente Y el dato existe y no es vacío. */
  showPhone: boolean
  showWhatsapp: boolean
  showEmail: boolean
  showAddress: boolean
  showAttentionHours: boolean
  showComplaintsContact: boolean

  /** Valores crudos listos para texto. '' si no hay dato. */
  phoneNumber: string
  whatsappNumber: string
  email: string
  address: string
  attentionHours: string
  complaintsContact: string

  /** Solo dígitos, listos para URLs tel: y https://wa.me/. */
  phoneDigits: string
  whatsappDigits: string

  /** True si al menos uno de los canales principales es visible. */
  hasAnyChannel: boolean
}

/**
 * Resuelve qué canales de contacto debe mostrar el template y prepara
 * los valores listos para renderizar.
 *
 * Reglas:
 *  - Un canal se muestra solo si su show_* es true Y el dato existe.
 *  - WhatsApp: hoy reutiliza show_phone y agent.phone. Cuando backend
 *    exponga show_whatsapp y whatsapp_number, este helper los consume
 *    sin que los templates cambien (?? fallback preparatorio).
 *  - phoneDigits y whatsappDigits quitan todo lo no-dígito para
 *    URLs limpias.
 *
 * @param agent Objeto completo del agente.
 * @returns Objeto con flags efectivos y valores listos.
 *
 * @example
 *   const contact = resolveContactChannels(agent)
 *   {contact.showPhone && (
 *     <a href={`tel:${contact.phoneDigits}`}>{contact.phoneNumber}</a>
 *   )}
 *   {contact.showWhatsapp && (
 *     <a href={`https://wa.me/${contact.whatsappDigits}`}
 *        target="_blank" rel="noopener noreferrer">WhatsApp</a>
 *   )}
 */
export function resolveContactChannels(
  agent: AgentFull
): ResolvedContactChannels {
  const phoneNumber = (agent.phone ?? '').trim()
  const whatsappNumber = (agent.whatsapp_number ?? agent.phone ?? '').trim()
  const email = (agent.email ?? '').trim()
  const address = (agent.physical_address ?? '').trim()
  const attentionHours = (agent.attention_hours ?? '').trim()
  const complaintsContact = (agent.complaints_contact ?? '').trim()

  // Hoy show_whatsapp aún no existe en backend → fallback a show_phone
  const rawShowWhatsapp = agent.show_whatsapp ?? agent.show_phone ?? false

  const showPhone = (agent.show_phone ?? false) && phoneNumber.length > 0
  const showWhatsapp = rawShowWhatsapp && whatsappNumber.length > 0
  const showEmail = (agent.show_email ?? false) && email.length > 0
  const showAddress = (agent.show_address ?? false) && address.length > 0
  const showAttentionHours =
    (agent.show_attention_hours ?? false) && attentionHours.length > 0
  const showComplaintsContact =
    (agent.show_complaints_contact ?? false) && complaintsContact.length > 0

  const phoneDigits = phoneNumber.replace(/[^0-9]/g, '')
  const whatsappDigits = whatsappNumber.replace(/[^0-9]/g, '')

  const hasAnyChannel = showPhone || showWhatsapp || showEmail || showAddress

  return {
    showPhone,
    showWhatsapp,
    showEmail,
    showAddress,
    showAttentionHours,
    showComplaintsContact,
    phoneNumber,
    whatsappNumber,
    email,
    address,
    attentionHours,
    complaintsContact,
    phoneDigits,
    whatsappDigits,
    hasAnyChannel,
  }
}

// ══════════════════════════════════════════════════════════════════════════
// resolveIdentityDisplay
// ══════════════════════════════════════════════════════════════════════════

export interface ResolvedIdentityDisplay {
  /**
   * Marca PERSONAL del agente.
   * Se usa en: biografía, sección "Sobre mí", firma del agente,
   * testimonios, CTA personal, tarjeta de contacto personal.
   */
  personal: {
    /** show_name && name existe */
    show: boolean
    /** Nombre sin prefijo, ej. "Juan Pérez". */
    name: string
    /** Con prefijo si aplica, ej. "Lic. Juan Pérez". */
    displayName: string
    /** "Lic.", "Arq.", etc., o null. */
    prefix: string | null
  }
  /**
   * Marca CORPORATIVA del agente.
   * Se usa en: header/navbar, footer, copyright, background difuminado,
   * watermark, branding corporativo.
   */
  corporate: {
    /** show_business_name && business_name existe */
    show: boolean
    /** Nombre comercial / razón social. */
    name: string
  }
  /** True si al menos una de las dos identidades es renderizable. */
  hasAnyIdentity: boolean
}

/**
 * Resuelve las DOS identidades del agente por SEPARADO: personal y
 * corporativa. Nunca se contraponen — viven en zonas distintas.
 *
 * @param agent Objeto completo del agente.
 * @returns Ambas identidades resueltas, cada una con su flag show.
 *
 * @example
 *   const id = resolveIdentityDisplay(agent)
 *   {id.personal.show && <h1>{id.personal.displayName}</h1>}
 *   {id.corporate.show && <span>© {id.corporate.name}</span>}
 */
export function resolveIdentityDisplay(
  agent: AgentFull
): ResolvedIdentityDisplay {
  const rawName = (agent.name ?? '').trim()
  const prefix = (agent.prefix ?? '').trim()
  const businessName = (agent.business_name ?? '').trim()

  const hasName = rawName.length > 0
  const hasBusiness = businessName.length > 0

  const personalShow = (agent.show_name ?? false) && hasName
  const corporateShow = (agent.show_business_name ?? false) && hasBusiness

  const displayName = prefix && hasName ? `${prefix} ${rawName}` : rawName

  return {
    personal: {
      show: personalShow,
      name: rawName,
      displayName,
      prefix: prefix.length > 0 ? prefix : null,
    },
    corporate: {
      show: corporateShow,
      name: businessName,
    },
    hasAnyIdentity: personalShow || corporateShow,
  }
}

// ══════════════════════════════════════════════════════════════════════════
// resolveImageStrategy
// ══════════════════════════════════════════════════════════════════════════

export interface ResolvedImageStrategy {
  /**
   * Imagen de marca PERSONAL del agente.
   * Se usa en: biografía, retrato del agente, "Sobre mí", testimonios.
   */
  personal: {
    /** show_avatar && avatar_url existe */
    show: boolean
    /** URL del avatar o null. */
    url: string | null
    /** 2 letras iniciales del name, para placeholder visual. */
    initialsFallback: string
  }
  /**
   * Imagen de marca CORPORATIVA.
   * Se usa en: header, footer, watermark, background difuminado.
   */
  corporate: {
    /** show_logo && logo_url existe */
    show: boolean
    /** URL del logo o null. */
    url: string | null
    /** 2 letras iniciales del business_name (o name como fallback). */
    initialsFallback: string
  }
  /** True si al menos una imagen es renderizable. */
  hasAnyImage: boolean
}

/**
 * Resuelve las DOS imágenes de identidad por SEPARADO.
 * Avatar (personal) y logo (corporativo) NUNCA van en el mismo lugar.
 *
 * @param agent Objeto completo del agente.
 * @returns Ambas imágenes resueltas, cada una con su flag show.
 *
 * @example
 *   const img = resolveImageStrategy(agent)
 *   {img.personal.show
 *     ? <img src={img.personal.url!} alt={agent.name} />
 *     : <div>{img.personal.initialsFallback}</div>}
 *   {img.corporate.show && <img src={img.corporate.url!} alt="logo" />}
 */
export function resolveImageStrategy(
  agent: AgentFull
): ResolvedImageStrategy {
  const avatarUrl = (agent.avatar_url ?? '').trim()
  const logoUrl = (agent.logo_url ?? '').trim()
  const name = agent.name ?? ''
  const businessName = agent.business_name ?? ''

  const personalShow = (agent.show_avatar ?? true) && avatarUrl.length > 0
  const corporateShow = (agent.show_logo ?? false) && logoUrl.length > 0

  return {
    personal: {
      show: personalShow,
      url: avatarUrl.length > 0 ? avatarUrl : null,
      initialsFallback: computeInitials(name),
    },
    corporate: {
      show: corporateShow,
      url: logoUrl.length > 0 ? logoUrl : null,
      initialsFallback: computeInitials(businessName || name),
    },
    hasAnyImage: personalShow || corporateShow,
  }
}

/**
 * Extrae 2 letras iniciales de un nombre para placeholder visual.
 *  - "Ana García"   → "AG"
 *  - "Anastasia"    → "AN"
 *  - "" o undefined → ""
 */
function computeInitials(fullName: string): string {
  const cleaned = fullName.trim()
  if (!cleaned) return ''
  const words = cleaned.split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  const first = words[0][0] ?? ''
  const last = words[words.length - 1][0] ?? ''
  return (first + last).toUpperCase()
}

// ══════════════════════════════════════════════════════════════════════════
// resolveLegalDocs
// ══════════════════════════════════════════════════════════════════════════

export interface ResolvedLegalDocs {
  showAvisoPrivacidad: boolean
  showTerminos: boolean
  showDerechosConsumidor: boolean
  showInfoNom247: boolean
  /** True si al menos un documento es visible. */
  hasAnyDoc: boolean
  /** Lista de documentos activos, lista para .map() en el footer. */
  availableDocs: LegalDocType[]
}

/**
 * Resuelve qué documentos legales debe exponer el footer del template.
 * info_nom247 requiere ADEMÁS que agent.nom_candidate === true.
 *
 * @param agent Objeto completo del agente.
 * @returns Flags individuales y array iterable.
 *
 * @example
 *   const docs = resolveLegalDocs(agent)
 *   {docs.availableDocs.map(type => (
 *     <button key={type} onClick={() => openLegalDoc(type)}>{type}</button>
 *   ))}
 */
export function resolveLegalDocs(agent: AgentFull): ResolvedLegalDocs {
  const showAvisoPrivacidad = agent.show_aviso_privacidad ?? false
  const showTerminos = agent.show_terminos ?? false
  const showDerechosConsumidor = agent.show_derechos_consumidor ?? false
  const showInfoNom247 =
    (agent.show_info_nom247 ?? false) && (agent.nom_candidate ?? false)

  const availableDocs: LegalDocType[] = []
  if (showAvisoPrivacidad) availableDocs.push('aviso_privacidad')
  if (showTerminos) availableDocs.push('terminos_condiciones')
  if (showDerechosConsumidor) availableDocs.push('derechos_consumidor')
  if (showInfoNom247) availableDocs.push('info_nom247')

  return {
    showAvisoPrivacidad,
    showTerminos,
    showDerechosConsumidor,
    showInfoNom247,
    hasAnyDoc: availableDocs.length > 0,
    availableDocs,
  }
}

// ══════════════════════════════════════════════════════════════════════════
// resolveChatConfig
// ══════════════════════════════════════════════════════════════════════════

export interface ChatTemplateDefaults {
  /**
   * Valor de showIA si el agente no tiene show_ia configurado.
   *  - Todos los templates excepto Showcase: true (default global).
   *  - Showcase (plan free, sin IA): pasa { defaultShowIA: false }.
   */
  defaultShowIA?: boolean
  /**
   * Segundos de delay antes de auto-abrir el chat.
   *  - OrbeTemplate: 4
   *  - Los demás: 0 (sin auto-abrir)
   */
  defaultDelay?: number
}

export interface ResolvedChatConfig {
  showIA: boolean
  delayIA: number
}

/**
 * Resuelve la configuración del ChatWidget para este agente/template.
 *
 * Precedencia (mayor a menor):
 *  1. agent.show_ia / agent.delay_ia si existen (futuro backend)
 *  2. templateDefaults.defaultShowIA / defaultDelay
 *  3. Constantes DEFAULT_SHOW_IA (true) / DEFAULT_CHAT_DELAY (0)
 *
 * @param agent Objeto del agente.
 * @param templateDefaults Overrides a nivel template.
 * @returns Configuración resuelta del chat.
 *
 * @example
 *   // ShowcaseTemplate (plan free, sin IA):
 *   const chat = resolveChatConfig(agent, { defaultShowIA: false })
 *
 *   // Template estándar:
 *   const chat = resolveChatConfig(agent)
 *
 *   // OrbeTemplate (auto-abrir a los 4s):
 *   const chat = resolveChatConfig(agent, { defaultDelay: 4 })
 */
export function resolveChatConfig(
  agent: AgentFull,
  templateDefaults?: ChatTemplateDefaults
): ResolvedChatConfig {
  const defaultShow = templateDefaults?.defaultShowIA ?? DEFAULT_SHOW_IA
  const defaultDelay = templateDefaults?.defaultDelay ?? DEFAULT_CHAT_DELAY

  const showIA = agent.show_ia ?? defaultShow
  const delayIA = agent.delay_ia ?? defaultDelay

  return { showIA, delayIA }
}

// ══════════════════════════════════════════════════════════════════════════
// useNomSeal — Hook para el Sello de Cumplimiento NOM-247
// ══════════════════════════════════════════════════════════════════════════

/**
 * Respuesta esperada del endpoint GET /api/public/nom/seal/:subdomain
 * No se exporta — uso interno del hook.
 */
interface NomSealApiResponse {
  success?: boolean
  seal_active?: boolean
  certificate_code?: string | null
  compliance_score?: number
}

/**
 * Estado del sello de cumplimiento NOM-247 para un agente.
 */
export interface NomSealState {
  /**
   * El sello debe renderizarse. Solo true cuando las tres condiciones
   * son simultáneamente verdaderas:
   *   1. agent.nom_candidate === true
   *   2. API responde seal_active === true
   *   3. Existe certificate_code válido
   */
  visible: boolean
  /** Código de certificado emitido por la autoridad técnica. */
  certCode: string | null
  /** URL pública para que el visitante verifique el certificado. */
  verifyUrl: string | null
  /** Ruta al asset SVG del sello, lista para usar en <img src>. */
  assetPath: string
  /** Copy listo para consumir: label, alt, link, tooltip, aria. */
  copy: typeof NOM_SEAL_COPY
  /** Estado de carga — útil para evitar flash durante el fetch. */
  loading: boolean
}

/**
 * Hook que resuelve el estado del sello de cumplimiento NOM-247.
 * Centraliza fetch, regla de visibilidad y copy para que los 10
 * templates lo consuman idéntico.
 *
 * FUENTE DE VERDAD:
 *  - Local: agent.nom_candidate. Si es false, no hace fetch.
 *  - Remota: GET /api/public/nom/seal/:subdomain → { seal_active,
 *    certificate_code }. El backend delega verificación técnica a
 *    CERNAG (entidad certificadora aliada).
 *
 * REGLA: el sello SOLO se muestra si las 3 condiciones son true.
 * Si falla cualquiera, visible=false. Un sello sin código verificable
 * es peor que no mostrar nada.
 *
 * LENGUAJE: el visitante ve "Cumplimiento NOM-247" (copy de
 * NOM_SEAL_COPY). CERNAG NO aparece en copy visible — solo en la URL
 * técnica de verificación.
 *
 * POSICIONAMIENTO (regla global para los 10 templates):
 *  - Top-right del layout
 *  - Tamaño responsive: 40-48px móvil, 56-64px tablet, 64-80px desktop
 *  - Medalla protagonista sin robar foco al agente o propiedades
 *  - Z-index ~40, nunca oculto en móvil (solo se escala)
 *
 * @param agent Objeto del agente.
 * @returns Estado del sello.
 *
 * @example
 *   const nomSeal = useNomSeal(agent)
 *   {nomSeal.visible && (
 *     <a href={nomSeal.verifyUrl!} target="_blank" rel="noopener noreferrer"
 *        aria-label={nomSeal.copy.ariaLabel} title={nomSeal.copy.tooltip}
 *        className="absolute top-4 right-4 md:top-6 md:right-6 z-40">
 *       <img src={nomSeal.assetPath} alt={nomSeal.copy.altText}
 *            className="h-10 sm:h-14 lg:h-16 xl:h-20 w-auto drop-shadow-md" />
 *     </a>
 *   )}
 */
export function useNomSeal(agent: AgentFull): NomSealState {
  const [state, setState] = useState<NomSealState>({
    visible: false,
    certCode: null,
    verifyUrl: null,
    assetPath: NOM_SEAL_ASSET_PATH,
    copy: NOM_SEAL_COPY,
    loading: !!agent.nom_candidate,
  })

  useEffect(() => {
    // Regla 1: si no es candidato, jamás se consulta la API.
    if (!agent.nom_candidate) {
      setState((s) => ({ ...s, visible: false, loading: false }))
      return
    }

    let cancelled = false

    fetch(API_ROUTES.nomSeal(getSubdomain()))
      .then((r) => (r.ok ? (r.json() as Promise<NomSealApiResponse>) : null))
      .then((data) => {
        if (cancelled) return

        const active = !!data?.seal_active
        const code = data?.certificate_code ?? null

        // Reglas 2 y 3: activo Y con código verificable.
        if (!active || !code) {
          setState((s) => ({ ...s, visible: false, loading: false }))
          return
        }

        setState((s) => ({
          ...s,
          visible: true,
          certCode: code,
          verifyUrl: NOM_CERT_VERIFY_URL(code),
          loading: false,
        }))
      })
      .catch(() => {
        if (cancelled) return
        setState((s) => ({ ...s, visible: false, loading: false }))
      })

    return () => {
      cancelled = true
    }
  }, [agent.nom_candidate])

  return state
}
